import React, { useEffect, useState } from 'react';
import axios from 'axios';

interface Customer {
  id: string;
  name: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  customerId: string | null;
  customer: Customer | null;
  createdAt: string;
}

const ROLES = [
  { value: 'SUPER_ADMIN', label: 'Super Admin', color: '#e74c3c' },
  { value: 'ADMIN', label: 'Admin', color: '#e67e22' },
  { value: 'MANAGER', label: 'Manager', color: '#3498db' },
  { value: 'CUSTOMER_ADMIN', label: 'Kunden-Admin', color: '#cc4e2e'  },
  { value: 'CUSTOMER_MANAGER', label: 'Kunden-Manager', color: '#2ecc71' },
  { value: 'CUSTOMER_VIEWER', label: 'Kunden-Viewer', color: '#95a5a6' },
]

const getRoleColor = (role: string) => ROLES.find(r => r.value === role)?.color || '#95a5a6'
const getRoleLabel = (role: string) => ROLES.find(r => r.value === role)?.label || role

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<User | null>(null);
  const [groupByCustomer, setGroupByCustomer] = useState(true);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('CUSTOMER_VIEWER');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');

  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isPointreef = ['SUPER_ADMIN', 'ADMIN', 'MANAGER'].includes(currentUser.role);
  const canDelete = ['SUPER_ADMIN', 'ADMIN', 'CUSTOMER_ADMIN'].includes(currentUser.role);

  const availableRoles = isPointreef
  ? ROLES
  : ROLES.filter(r => ['CUSTOMER_ADMIN', 'CUSTOMER_MANAGER', 'CUSTOMER_VIEWER'].includes(r.value));

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchCustomers();
  }, []); // eslint-disable-line

  const resetForm = () => {
    setEmail(''); setFirstName(''); setLastName('');
    setRole('CUSTOMER_VIEWER'); setPassword(''); setPasswordConfirm('');
    setCurrentPassword(''); setSelectedCustomerId(''); setError('');
  };

  const openCreate = () => {
    setEditUser(null);
    resetForm();
    if (['CUSTOMER_ADMIN', 'CUSTOMER_MANAGER'].includes(currentUser.role)) {
      setSelectedCustomerId(currentUser.customerId || '');
    }
    setShowForm(true);
  };

  const openEdit = (u: User) => {
    setEditUser(u);
    setEmail(u.email);
    setFirstName(u.firstName);
    setLastName(u.lastName);
    setRole(u.role);
    setPassword('');
    setPasswordConfirm('');
    setCurrentPassword('');
    setSelectedCustomerId(u.customerId || '');
    setError('');
    setShowForm(true);
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');

    // Passwort Validierung
    if (password && password !== passwordConfirm) {
      setError('Passwörter stimmen nicht überein');
      setLoading(false);
      return;
    }
    if (password && password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      setLoading(false);
      return;
    }

    try {
      if (editUser) {
        const isSelf = editUser.id === currentUser.id;
        const payload: any = { firstName, lastName, email, role, customerId: selectedCustomerId || null };
        if (password) {
          payload.password = password;
          if (isSelf) payload.currentPassword = currentPassword;
        }
        await axios.patch(`${process.env.REACT_APP_API_URL}/api/users/${editUser.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (isSelf) {
          const updatedUser = { ...currentUser, firstName, lastName, email };
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
      } else {
        if (!email || !firstName || !lastName || !password) {
          setError('Bitte alle Pflichtfelder ausfüllen');
          setLoading(false);
          return;
        }
        await axios.post(`${process.env.REACT_APP_API_URL}/api/users`, {
          email, firstName, lastName, role, password,
          customerId: selectedCustomerId || null
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      setShowForm(false);
      setEditUser(null);
      resetForm();
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/users/${deleteConfirm.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteConfirm(null);
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  const filteredUsers = users.filter(u => {
    const q = search.toLowerCase();
    return (
      u.firstName?.toLowerCase().includes(q) ||
      u.lastName?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.role?.toLowerCase().includes(q) ||
      u.customer?.name?.toLowerCase().includes(q)
    );
  });

  const grouped: Record<string, User[]> = {};
  filteredUsers.forEach(u => {
    const key = u.customer?.name || 'pointreef (intern)';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(u);
  });

  const sortedGroups = Object.keys(grouped).sort((a, b) => {
    if (a.includes('intern')) return -1;
    if (b.includes('intern')) return 1;
    return a.localeCompare(b);
  });

  const needsCustomer = ['CUSTOMER_ADMIN', 'CUSTOMER_MANAGER', 'CUSTOMER_VIEWER'].includes(role);
  const isSelf = editUser?.id === currentUser.id;
  const passwordsMatch = password && passwordConfirm && password === passwordConfirm;
  const passwordsMismatch = password && passwordConfirm && password !== passwordConfirm;

  return (
    <div style={s.container}>
      <div style={s.sidebar}>
        <div style={{ padding: '0 16px 24px 16px', textAlign: 'center' }}>
          <img src="/portalis_logo.png" alt="Portalis" style={{ width: '160px' }} />
        </div>
        <nav style={s.nav}>
          <a href="/dashboard" style={s.navItem}>Startseite</a>
          <a href="/projects" style={s.navItem}>Liegenschaften</a>
          <a style={s.navItemActive}>Benutzer</a>
          {isPointreef && <a href="/customers" style={s.navItem}>Kunden</a>}
        </nav>
        <div style={s.userInfo}>
          <p style={s.userName}>{currentUser.firstName} {currentUser.lastName}</p>
          <p style={s.userRole}>{currentUser.role}</p>
          <button style={s.logoutButton} onClick={() => { localStorage.clear(); window.location.href = '/login'; }}>
            Abmelden
          </button>
        </div>
      </div>

      <div style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Benutzer</h1>
            <p style={s.subtitle}>{users.length} Benutzer</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {isPointreef && (
              <button
                style={{ ...s.toggleBtn, backgroundColor: groupByCustomer ? '#00c49a' : 'white', color: groupByCustomer ? 'white' : '#666' }}
                onClick={() => setGroupByCustomer(!groupByCustomer)}
              >
                Nach Kunde gruppieren
              </button>
            )}
            <button style={s.addButton} onClick={openCreate}>+ Neuer Benutzer</button>
          </div>
        </div>

        <input
          style={{ ...s.input, marginBottom: '16px', width: '100%', boxSizing: 'border-box' }}
          placeholder="Name, E-Mail oder Rolle suchen..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        {showForm && (
          <div style={s.form}>
            <h3 style={s.formTitle}>{editUser ? 'Benutzer bearbeiten' : 'Neuen Benutzer anlegen'}</h3>
            <div style={s.formGrid}>

              {/* Name */}
              <div style={s.inputGroup}>
                <label style={s.label}>Vorname *</label>
                <input style={s.input} value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Max" />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Nachname *</label>
                <input style={s.input} value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Mustermann" />
              </div>

              {/* Email */}
              <div style={s.inputGroup}>
                <label style={s.label}>E-Mail *</label>
                <input style={s.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="max@beispiel.de" />
              </div>

              {/* Passwort */}
              {editUser ? (
                <>
                  {isSelf && (
                    <div style={s.inputGroup}>
                      <label style={s.label}>Aktuelles Passwort</label>
                      <input
                        style={s.input}
                        type="password"
                        value={currentPassword}
                        onChange={e => setCurrentPassword(e.target.value)}
                        placeholder="Nur ausfüllen wenn Passwort geändert wird"
                      />
                    </div>
                  )}
                  <div style={s.inputGroup}>
                    <label style={s.label}>Neues Passwort <span style={{ color: '#aaa', fontWeight: 'normal' }}>(optional)</span></label>
                    <input
                      style={s.input}
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Leer lassen um Passwort beizubehalten"
                    />
                  </div>
                  <div style={s.inputGroup}>
                    <label style={s.label}>Neues Passwort wiederholen</label>
                    <input
                      style={{
                        ...s.input,
                        borderColor: passwordsMismatch ? '#e74c3c' : passwordsMatch ? '#00c49a' : '#ddd'
                      }}
                      type="password"
                      value={passwordConfirm}
                      onChange={e => setPasswordConfirm(e.target.value)}
                      placeholder="Passwort wiederholen"
                    />
                    {passwordsMismatch && (
                      <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#e74c3c' }}>Passwörter stimmen nicht überein</p>
                    )}
                    {passwordsMatch && (
                      <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#00c49a' }}>Passwörter stimmen überein</p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div style={s.inputGroup}>
                    <label style={s.label}>Passwort *</label>
                    <input
                      style={s.input}
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Mindestens 8 Zeichen"
                    />
                  </div>
                  <div style={s.inputGroup}>
                    <label style={s.label}>Passwort wiederholen *</label>
                    <input
                      style={{
                        ...s.input,
                        borderColor: passwordsMismatch ? '#e74c3c' : passwordsMatch ? '#00c49a' : '#ddd'
                      }}
                      type="password"
                      value={passwordConfirm}
                      onChange={e => setPasswordConfirm(e.target.value)}
                      placeholder="Passwort wiederholen"
                    />
                    {passwordsMismatch && (
                      <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#e74c3c' }}>Passwörter stimmen nicht überein</p>
                    )}
                    {passwordsMatch && (
                      <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#00c49a' }}>Passwörter stimmen überein</p>
                    )}
                  </div>
                </>
              )}

              {/* Rolle */}
              <div style={s.inputGroup}>
                <label style={s.label}>Rolle</label>
                <select style={{ ...s.input, backgroundColor: isSelf ? '#f9f9f9' : 'white' }} value={role} onChange={e => setRole(e.target.value)} disabled={!!isSelf}>
                  {availableRoles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                {isSelf && <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#aaa' }}>Eigene Rolle kann nicht geändert werden</p>}
              </div>

              {/* Kunde */}
              {isPointreef && (
                <div style={s.inputGroup}>
                  <label style={s.label}>
                    Kunde {needsCustomer && <span style={{ color: '#e74c3c' }}>*</span>}
                    {!needsCustomer && <span style={{ color: '#aaa', fontWeight: 'normal' }}> (optional)</span>}
                  </label>
                  <select style={s.input} value={selectedCustomerId} onChange={e => setSelectedCustomerId(e.target.value)}>
                    <option value="">pointreef (intern)</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
            </div>

            {error && <p style={s.errorText}>{error}</p>}
            <div style={s.formButtons}>
              <button style={s.cancelButton} onClick={() => { setShowForm(false); setEditUser(null); resetForm(); }}>Abbrechen</button>
              <button style={{ ...s.saveButton, opacity: loading ? 0.7 : 1 }} onClick={handleSave} disabled={loading}>
                {loading ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        )}

        {groupByCustomer && isPointreef ? (
          sortedGroups.map(group => (
            <div key={group} style={s.groupCard}>
              <div style={s.groupHeader}>
                <h3 style={s.groupTitle}>{group}</h3>
                <span style={s.groupCount}>{grouped[group].length} Benutzer</span>
              </div>
              <div>
                {grouped[group].map(u => (
                  <UserRow key={u.id} u={u} currentUserId={currentUser.id} canDelete={canDelete}
                    onEdit={openEdit} onDelete={setDeleteConfirm} />
                ))}
              </div>
            </div>
          ))
        ) : (
          <div style={s.groupCard}>
            {filteredUsers.map(u => (
              <UserRow key={u.id} u={u} currentUserId={currentUser.id} canDelete={canDelete}
                onEdit={openEdit} onDelete={setDeleteConfirm} />
            ))}
          </div>
        )}
      </div>

      {deleteConfirm && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <p style={{ fontSize: '18px', fontWeight: '600', color: '#0f2744', margin: '0 0 8px 0' }}>Benutzer löschen</p>
            <p style={{ color: '#666', fontSize: '14px', margin: '0 0 24px 0' }}>
              Möchtest du <strong>{deleteConfirm.firstName} {deleteConfirm.lastName}</strong> wirklich löschen?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={() => setDeleteConfirm(null)} style={s.cancelButton}>Abbrechen</button>
              <button onClick={handleDelete} style={s.deleteButton}>Löschen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface UserRowProps {
  u: User;
  currentUserId: string;
  canDelete: boolean;
  onEdit: (u: User) => void;
  onDelete: (u: User) => void;
}

const UserRow: React.FC<UserRowProps> = ({ u, currentUserId, canDelete, onEdit, onDelete }) => (
  <div style={ur.row}>
    <div style={ur.avatar}>{u.firstName[0]}{u.lastName[0]}</div>
    <div style={{ flex: 1 }}>
      <p style={ur.name}>
        {u.firstName} {u.lastName}
        {u.id === currentUserId && <span style={ur.youBadge}>Du</span>}
      </p>
      <p style={ur.email}>{u.email}</p>
    </div>
    <span style={{ ...ur.roleBadge, backgroundColor: getRoleColor(u.role) + '20', color: getRoleColor(u.role) }}>
      {getRoleLabel(u.role)}
    </span>
    <p style={ur.date}>{new Date(u.createdAt).toLocaleDateString('de-DE')}</p>
    <div style={{ display: 'flex', gap: '4px' }}>
      <button style={ur.iconBtn} onClick={() => onEdit(u)} title="Bearbeiten">✏️</button>
      {canDelete && u.id !== currentUserId && (
        <button style={ur.iconBtn} onClick={() => onDelete(u)} title="Löschen">🗑️</button>
      )}
    </div>
  </div>
);

const s: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', height: '100vh', backgroundColor: '#f5f5f5' },
  sidebar: { width: '220px', backgroundColor: '#0f2744', display: 'flex', flexDirection: 'column', padding: '24px 0', flexShrink: 0 },
  nav: { display: 'flex', flexDirection: 'column', flex: 1 },
  navItem: { color: '#aac4e0', padding: '12px 24px', cursor: 'pointer', fontSize: '14px', textDecoration: 'none', display: 'block' },
  navItemActive: { color: 'white', padding: '12px 24px', fontSize: '14px', backgroundColor: '#1e3a5f', borderLeft: '3px solid #00c49a', display: 'block' },
  userInfo: { padding: '24px', borderTop: '1px solid #1e3a5f' },
  userName: { color: 'white', margin: '0', fontSize: '14px', fontWeight: '600' },
  userRole: { color: '#00c49a', margin: '4px 0 12px 0', fontSize: '12px' },
  logoutButton: { backgroundColor: 'transparent', border: '1px solid #aac4e0', color: '#aac4e0', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', width: '100%' },
  main: { flex: 1, padding: '32px', overflow: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' },
  title: { fontSize: '28px', color: '#0f2744', margin: '0' },
  subtitle: { color: '#888', margin: '4px 0 0 0' },
  addButton: { backgroundColor: '#00c49a', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 18px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  toggleBtn: { border: '1px solid #ddd', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' },
  form: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  formTitle: { margin: '0 0 20px 0', color: '#0f2744' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#333' },
  input: { padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', fontFamily: 'inherit' },
  formButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' },
  cancelButton: { backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '8px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  saveButton: { backgroundColor: '#00c49a', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  deleteButton: { backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  errorText: { color: '#e74c3c', fontSize: '13px', margin: '8px 0 0 0' },
  groupCard: { backgroundColor: 'white', borderRadius: '12px', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' },
  groupHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #f0f0f0', backgroundColor: '#fafafa' },
  groupTitle: { margin: '0', color: '#0f2744', fontSize: '15px' },
  groupCount: { backgroundColor: '#f0faf7', color: '#00c49a', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '32px', maxWidth: '420px', width: '90%', textAlign: 'center' },
};

const ur: { [key: string]: React.CSSProperties } = {
  row: { display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 20px', borderBottom: '1px solid #f5f5f5' },
  avatar: { width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#0f2744', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '600', flexShrink: 0 },
  name: { margin: '0 0 2px 0', color: '#333', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' },
  email: { margin: '0', color: '#aaa', fontSize: '12px' },
  youBadge: { backgroundColor: '#f0faf7', color: '#00c49a', padding: '1px 6px', borderRadius: '8px', fontSize: '11px', fontWeight: '600' },
  roleBadge: { padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' as const, flexShrink: 0 },
  date: { color: '#aaa', fontSize: '12px', margin: '0', flexShrink: 0 },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px', padding: '2px 4px' },
};

export default Users;
